import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Inbox, Search } from 'lucide-react';
import {
  myFindingsService,
  type MyFinding,
} from '../../services/auditService';
import FindingDetailModal from '../../components/audits/FindingDetailModal';
import { AuditStatusBadge } from './AuditStatusBadge';
import FormControl from '../../components/common/FormControl';
import Pagination from '../../components/common/Pagination';
import Toast, { ToastMessage } from '../../components/common/Toast';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const RELATION_LABELS: Record<string, { label: string; color: string }> = {
  raised: { label: 'Raised by me', color: '#0ea5e9' },
  to_respond: { label: 'Awaiting my response', color: '#f97316' },
  both: { label: 'Raised by me · awaiting my response', color: '#7c3aed' },
  other: { label: '', color: '#6b7280' },
};

function deriveFindingState(f: MyFinding): 'Raised' | 'Responded' | 'Closed' {
  if (f.is_closed) return 'Closed';
  if (f.responded_at) return 'Responded';
  return 'Raised';
}

const MyFindingsContent: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const [rows, setRows] = useState<MyFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [openFinding, setOpenFinding] = useState<MyFinding | null>(null);
  const [relationFilter, setRelationFilter] = useState<string>('all');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fetchData = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    myFindingsService
      .list()
      .then((resp) => {
        if (cancelled) return;
        setRows(resp.data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to load your findings.';
        setToast({ type: 'error', message: typeof msg === 'string' ? msg : 'Failed to load your findings.' });
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData]);

  useEffect(() => { setPage(1); }, [searchTerm, relationFilter, stateFilter, pageSize]);

  const filtered = rows.filter((r) => {
    if (relationFilter !== 'all' && r.relation !== relationFilter) return false;
    if (stateFilter !== 'all' && deriveFindingState(r) !== stateFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const text = [r.findings_text, r.program_name, r.procedure_name].filter(Boolean).join(' ').toLowerCase();
      if (!text.includes(q)) return false;
    }
    return true;
  });

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">

      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            data-testid="SEL_my_findings_relation_filter"
            value={relationFilter}
            onChange={v => setRelationFilter(String(v ?? 'all'))}
            className="filter-status"
            noSearchIcon
            options={[
              { value: 'all', label: 'All relations' },
              { value: 'raised', label: 'Raised by me' },
              { value: 'to_respond', label: 'Awaiting my response' },
              { value: 'both', label: 'Both' },
            ]}
          />
          <FormControl.SearchSelect
            data-testid="SEL_my_findings_state_filter"
            value={stateFilter}
            onChange={v => setStateFilter(String(v ?? 'all'))}
            className="filter-status"
            noSearchIcon
            options={[
              { value: 'all', label: 'All states' },
              { value: 'Raised', label: 'Raised' },
              { value: 'Responded', label: 'Responded' },
              { value: 'Closed', label: 'Closed' },
            ]}
          />
          <FormControl.Input
            type="text"
            placeholder="Search…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          {loading ? (
            <div style={{ padding: 20, color: '#6b7280', fontSize: 13 }}>Loading…</div>
          ) : paginated.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 40, color: 'var(--text-tertiary)' }}>
              <Inbox size={36} style={{ opacity: 0.5 }} />
              <span style={{ fontSize: 13 }}>No findings match.</span>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, paddingRight: 4 }}>
              {paginated.map((row) => {
                const rel = RELATION_LABELS[row.relation] ?? RELATION_LABELS.other;
                return (
                  <div
                    key={row.id}
                    data-testid={`BTN_my_findings_row_${row.id}_open`}
                    className="netbody-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenFinding(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenFinding(row);
                      }
                    }}
                    style={{
                      padding: 12,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      cursor: 'pointer',
                      borderLeft: `3px solid ${rel.color}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: rel.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {rel.label}
                      </span>
                      <AuditStatusBadge state={deriveFindingState(row)} size="sm" />
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {row.findings_text || `Finding #${row.id}`}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {[row.program_name, row.procedure_name].filter(Boolean).join(' · ') || '—'}
                    </div>
                    <div
                      style={{
                        marginTop: 'auto',
                        paddingTop: 8,
                        borderTop: '1px solid #f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6,
                      }}
                    >
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={11} />
                        {formatDate(row.raised_on_date)}
                      </span>
                      {row.is_trivial ? (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280' }}>TRIVIAL</span>
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#b91c1c' }}>MATERIAL</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            recordLabel="findings"
          />
        </div>
      </div>

      <FindingDetailModal
        isOpen={openFinding !== null}
        onClose={() => {
          setOpenFinding(null);
          fetchData();
        }}
        finding={openFinding}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default MyFindingsContent;
